import { signal, computed } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ulid, type Envelope } from '@foyer/shared';
import * as api from './lib/api.js';
import { ApiError } from './lib/api.js';
import { WsClient, connectionState } from './lib/ws-client.js';
import {
  getMessages,
  putMessage,
  markReceipt,
  clearPending,
  type StoredMessage,
  type StoredMedia,
} from './lib/db.js';
import { encodePlainPayload, decodePlainPayload } from './lib/payload.js';
import { compressImage, sha256Hex, downloadObjectUrl, formatBytes } from './lib/image.js';
import { ImageViewer } from './components/ImageViewer.js';
import { Settings } from './components/Settings.js';
import { SettingsIcon, CameraIcon, SendIcon, CloseIcon } from './components/Icons.js';
import { LockScreen } from './components/LockScreen.js';
import { isLocked } from './lib/applock.js';
import { t, tError } from './lib/i18n.js';

/** WhatsApp sends up to 10 photos as one album — matches MessageBodySchema's cap. */
const MAX_PHOTOS_PER_SEND = 10;

type Session = {
  accessToken: string;
  userId: string;
  handle: string;
  displayName: string;
  isAdmin: boolean;
};

const session = signal<Session | null>(null);
const bootChecked = signal(false);
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The access token is short-lived (15m, ADR-0007) and nothing was renewing
 * it during active use — found live: the app only ever called /api/auth/refresh
 * once, at boot, so any session open longer than that window (or a
 * backgrounded phone/PWA resumed later) silently started failing every
 * authed request, and the next full reload found a stale/rotated cookie and
 * fell back to a real login screen. Refresh proactively at 80% of the
 * token's lifetime, and immediately whenever the app becomes visible again.
 */
function applySession(auth: {
  accessToken: string;
  expiresIn: number;
  user: { id: string; handle: string; displayName: string; isAdmin: boolean };
}) {
  session.value = {
    accessToken: auth.accessToken,
    userId: auth.user.id,
    handle: auth.user.handle,
    displayName: auth.user.displayName,
    isAdmin: auth.user.isAdmin,
  };
  if (refreshTimer) clearTimeout(refreshTimer);
  const delayMs = Math.max(5000, auth.expiresIn * 0.8 * 1000);
  refreshTimer = setTimeout(silentRefresh, delayMs);
}

async function silentRefresh() {
  const auth = await api.refresh();
  if (auth) applySession(auth);
  else session.value = null; // refresh token itself is gone — a real login is unavoidable
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && session.value) void silentRefresh();
  });
}

async function boot() {
  const auth = await api.refresh();
  if (auth) applySession(auth);
  bootChecked.value = true;
}

const connectionLabel = computed(() => {
  switch (connectionState.value) {
    case 'open':
      return t('conn.open');
    case 'connecting':
      return t('conn.connecting');
    case 'reconnecting':
      return t('conn.reconnecting');
    case 'closed':
      return t('conn.closed');
  }
});

export function App() {
  useEffect(() => {
    void boot();
  }, []);

  if (!bootChecked.value) {
    return (
      <main className="shell">
        <p className="status status--checking">{t('app.loading')}</p>
      </main>
    );
  }

  if (!session.value) return <AuthScreen />;
  if (isLocked.value) return <LockScreen />;
  return view.value === 'settings' ? (
    <Settings
      accessToken={session.value.accessToken}
      displayName={session.value.displayName}
      onBack={() => (view.value = 'chat')}
    />
  ) : (
    <ChatScreen s={session.value} />
  );
}

const view = signal<'chat' | 'settings'>('chat');

function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [displayName, setDisplayName] = useState('');

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const auth =
        mode === 'login'
          ? await api.login({ handle, password, deviceLabel: guessDeviceLabel() })
          : await api.register({
              inviteCode,
              handle,
              displayName,
              password,
              deviceLabel: guessDeviceLabel(),
            });
      applySession(auth);
    } catch (err) {
      const reqIdSuffix = err instanceof ApiError && err.reqId ? ` (${err.reqId})` : '';
      const msg = err instanceof ApiError ? tError(err.code, err.message) : t('err.generic');
      setError(msg + reqIdSuffix);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="shell">
      <h1>Foyer</h1>
      <p className="tagline">{t('app.tagline')}</p>

      <div className="tabs">
        <button
          type="button"
          className={mode === 'login' ? 'tab tab--active' : 'tab'}
          onClick={() => setMode('login')}
        >
          {t('auth.signIn')}
        </button>
        <button
          type="button"
          className={mode === 'register' ? 'tab tab--active' : 'tab'}
          onClick={() => setMode('register')}
        >
          {t('auth.haveInvite')}
        </button>
      </div>

      <form className="auth-form" onSubmit={submit}>
        {mode === 'register' && (
          <>
            <label>
              {t('auth.inviteCode')}
              <input
                value={inviteCode}
                onInput={(e) => setInviteCode((e.target as HTMLInputElement).value)}
                required
              />
            </label>
            <label>
              {t('auth.yourName')}
              <input
                value={displayName}
                onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)}
                required
              />
            </label>
          </>
        )}
        <label>
          {t('auth.handle')}
          <input
            value={handle}
            onInput={(e) => setHandle((e.target as HTMLInputElement).value.toLowerCase())}
            autocomplete="username"
            required
          />
        </label>
        <label>
          {t('auth.password')}
          <input
            type="password"
            value={password}
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            autocomplete={mode === 'login' ? 'current-password' : 'new-password'}
            minLength={mode === 'register' ? 10 : undefined}
            required
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? t('auth.pleaseWait') : mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}
        </button>
      </form>
    </main>
  );
}

function guessDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return t('device.android');
  if (/iphone|ipad/i.test(ua)) return t('device.ios');
  return t('device.browser');
}

function ChatScreen({ s }: { s: Session }) {
  const [convId, setConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<StoredMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    phase: 'compressing' | 'uploading';
    index: number;
    total: number;
  } | null>(null);
  const [viewer, setViewer] = useState<{ media: StoredMedia[]; startIndex: number } | null>(null);
  const [bubbleMenu, setBubbleMenu] = useState<{ message: StoredMessage; media?: StoredMedia } | null>(
    null,
  );
  const [replyingTo, setReplyingTo] = useState<StoredMessage | null>(null);
  const [pendingPhotos, setPendingPhotos] = useState<{ files: File[]; previewUrls: string[] } | null>(
    null,
  );
  const wsRef = useRef<WsClient | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const prevConnRef = useRef(connectionState.value);
  const incomingChainRef = useRef<Promise<void>>(Promise.resolve());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function cancelLongPress() {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  }
  function startLongPress(action: () => void) {
    cancelLongPress();
    longPressTimer.current = setTimeout(action, 500);
  }
  /** What a reply quote shows for the original — no sender-name lookup
   * exists client-side yet (single shared conversation, M3 scope), so this
   * stays to just the content itself. */
  function quotedExcerpt(id: string): string {
    const original = messages.find((mm) => mm.id === id);
    if (!original) return t('reply.unavailable');
    if (original.kind === 'image') {
      return original.text ? `📷 ${original.text}` : `📷 ${t('reply.photo')}`;
    }
    return original.text.slice(0, 140);
  }
  async function downloadPhoto(med: StoredMedia) {
    setBubbleMenu(null);
    try {
      const url = await api.fetchMediaObjectUrl(s.accessToken, med.id);
      downloadObjectUrl(url, med.id, med.mime);
      URL.revokeObjectURL(url);
    } catch {
      // Same-origin, already-authenticated fetch — a failure here is rare
      // enough (network drop mid-tap) not to need a dedicated error toast.
    }
  }

  // Load which conversation we're in (M3 scope: exactly one shared "Family" room).
  useEffect(() => {
    api.listConversations(s.accessToken).then(({ conversations }) => {
      if (conversations[0]) setConvId(conversations[0].id);
    });
  }, [s.accessToken]);

  useEffect(() => {
    if (!convId) return;
    getMessages(convId).then(async (loaded) => {
      setMessages(loaded);
      // The WS connection can reach 'open' before this IndexedDB load
      // finishes — the reconnect-triggered resend below is edge-triggered
      // and only fires once per transition, so a pending message that
      // wasn't in `messages` yet at that exact moment would otherwise never
      // get resent (found via a message stuck on "sending…" indefinitely).
      if (connectionState.value === 'open') resendPending(loaded);

      // Issue #9: local storage is the only place history ever lived. A
      // device that never saw a message live (new account, or one that lost
      // IndexedDB — e.g. a browser "clear site data") had no way to catch
      // up. Best-effort: local storage already has whatever this device has
      // seen, so a failed fetch just leaves it as-is.
      try {
        const { messages: history } = await api.fetchHistory(s.accessToken, convId);
        for (const envelope of history) {
          await putMessage(envelopeToStored(envelope));
        }
        if (history.length > 0) setMessages(await getMessages(convId));
      } catch {
        // network hiccup or not yet authed — next load tries again
      }
    });
  }, [convId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  // Connect once we know who we are; token getter always reads the current signal.
  useEffect(() => {
    const client = new WsClient(
      () => session.value?.accessToken ?? null,
      {
        onMessage: (envelope) => {
          // Chained, not fire-and-forget: the server can send several
          // 'message' frames back-to-back while draining the offline queue,
          // immediately followed by 'synced'. Each handleIncoming() does an
          // async IndexedDB write; without this chain, 'synced' could
          // trigger its re-fetch before the last write(s) finished, silently
          // missing them from the render with nothing to re-trigger it
          // afterward — found live: messages sent while offline sometimes
          // never appeared even after reconnecting.
          incomingChainRef.current = incomingChainRef.current.then(() => handleIncoming(envelope));
        },
        onAck: (messageId) => {
          void clearPending(messageId);
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, pending: false } : m)));
        },
        onReceipt: (messageId, _userId, kind, at) => {
          void markReceipt(messageId, kind, at);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId ? { ...m, [kind === 'delivered' ? 'deliveredAt' : 'readAt']: at } : m,
            ),
          );
        },
        onSynced: (count) => {
          if (count === 0 || !convId) return;
          // Wait for every already-queued incoming message to finish being
          // written before trusting a fresh read to have all of them.
          incomingChainRef.current = incomingChainRef.current.then(() =>
            getMessages(convId).then(setMessages),
          );
        },
      },
    );
    wsRef.current = client;
    client.connect();
    return () => client.close();
    // Deliberately empty deps: connect once per mount. The token getter reads
    // the live session signal, so it doesn't need `s` in the dependency list.
  }, []);

  function resendPending(list: StoredMessage[]) {
    for (const m of list.filter((m) => m.pending)) {
      wsRef.current?.send({ t: 'send', envelope: storedToEnvelope(m) });
    }
  }

  // Reconnected after being offline: resend anything still marked pending.
  useEffect(() => {
    const was = prevConnRef.current;
    prevConnRef.current = connectionState.value;
    if (was !== 'open' && connectionState.value === 'open') resendPending(messages);
  }, [connectionState.value, messages]);

  function envelopeToStored(envelope: Envelope): StoredMessage {
    const body = decodePlainPayload(envelope.payload);
    return {
      id: envelope.id,
      convId: envelope.convId,
      senderId: envelope.senderId,
      ts: envelope.ts,
      kind: body.kind === 'image' ? 'image' : 'text',
      text: body.text ?? '',
      media: body.media as StoredMedia[] | undefined,
      replyTo: envelope.replyTo,
    };
  }

  async function handleIncoming(envelope: Envelope) {
    if (envelope.senderId === s.userId) return; // our own message, already stored optimistically
    const stored = envelopeToStored(envelope);
    await putMessage(stored);
    // IndexedDB's put() already dedupes by id, but React state doesn't — a
    // resent 'message' frame (e.g. the sender's own stale-pending resend
    // after their reconnect) would otherwise render as a second bubble even
    // though only one row exists underneath. Found via a real duplicate-
    // messages report, not by inspection.
    setMessages((prev) => (prev.some((m) => m.id === stored.id) ? prev : [...prev, stored]));
    wsRef.current?.send({ t: 'receipt', messageIds: [envelope.id], kind: 'delivered' });
    wsRef.current?.send({ t: 'receipt', messageIds: [envelope.id], kind: 'read' });
  }

  function storedToEnvelope(m: StoredMessage): Envelope {
    return {
      v: 1,
      id: m.id,
      convId: m.convId,
      senderId: m.senderId,
      ts: m.ts,
      alg: 'plain',
      payload: encodePlainPayload(
        m.kind === 'image'
          ? {
              kind: 'image',
              text: m.text || undefined,
              media: m.media?.map((med) => ({
                id: med.id,
                mime: med.mime,
                bytes: med.bytes ?? 0,
                width: med.width,
                height: med.height,
                thumb: med.thumb,
              })),
            }
          : { kind: 'text', text: m.text },
      ),
      ...(m.media?.length ? { mediaIds: m.media.map((med) => med.id) } : {}),
      ...(m.replyTo ? { replyTo: m.replyTo } : {}),
    };
  }

  async function sendMessage(text: string, replyTo?: string) {
    if (!text || !convId) return;
    const stored: StoredMessage = {
      id: ulid(),
      convId,
      senderId: s.userId,
      ts: Date.now(),
      kind: 'text',
      text,
      pending: true,
      ...(replyTo ? { replyTo } : {}),
    };
    await putMessage(stored);
    setMessages((prev) => [...prev, stored]);
    wsRef.current?.send({ t: 'send', envelope: storedToEnvelope(stored) });
  }

  async function sendPhotos(files: File[], caption: string, replyTo?: string) {
    if (files.length === 0 || !convId) return;
    const picked = files.slice(0, MAX_PHOTOS_PER_SEND);

    const media: StoredMedia[] = [];
    try {
      for (let i = 0; i < picked.length; i++) {
        const file = picked[i]!;
        const total = picked.length;
        setUploadProgress({ phase: 'compressing', index: i + 1, total });
        const compressed = await compressImage(file);
        const sha256 = await sha256Hex(compressed.blob);
        const mediaId = ulid();
        setUploadProgress({ phase: 'uploading', index: i + 1, total });
        await api.uploadMedia(
          s.accessToken,
          mediaId,
          sha256,
          compressed.blob,
          compressed.width,
          compressed.height,
        );
        media.push({
          id: mediaId,
          mime: compressed.blob.type,
          bytes: compressed.blob.size,
          width: compressed.width,
          height: compressed.height,
          thumb: compressed.thumb,
        });
      }
    } finally {
      setUploadProgress(null);
    }

    const stored: StoredMessage = {
      id: ulid(),
      convId,
      senderId: s.userId,
      ts: Date.now(),
      kind: 'image',
      text: caption,
      media,
      pending: true,
      ...(replyTo ? { replyTo } : {}),
    };
    await putMessage(stored);
    setMessages((prev) => [...prev, stored]);
    wsRef.current?.send({ t: 'send', envelope: storedToEnvelope(stored) });
  }

  async function submitComposer(e: Event) {
    e.preventDefault();
    const replyTo = replyingTo?.id;
    if (pendingPhotos) {
      const { files, previewUrls } = pendingPhotos;
      const caption = draft.trim();
      setDraft('');
      setReplyingTo(null);
      setPendingPhotos(null);
      previewUrls.forEach((u) => URL.revokeObjectURL(u));
      await sendPhotos(files, caption, replyTo);
    } else {
      const text = draft.trim();
      if (!text) return;
      setDraft('');
      setReplyingTo(null);
      await sendMessage(text, replyTo);
    }
  }

  async function copyInvite() {
    try {
      const { code } = await api.createInvite(s.accessToken);
      setInviteMsg(t('chat.inviteCreated', { code }));
    } catch (err) {
      const msg = err instanceof ApiError ? tError(err.code, err.message) : t('chat.inviteFailed');
      setInviteMsg(msg);
    }
  }

  async function signOut() {
    await api.logout(s.accessToken).catch(() => {});
    if (refreshTimer) clearTimeout(refreshTimer);
    session.value = null;
  }

  return (
    <main className="chat-shell">
      <header className="chat-header">
        <div>
          <strong>{t('chat.familyTitle')}</strong>
          <span className={`conn conn--${connectionState.value}`}>{connectionLabel.value}</span>
        </div>
        <div className="chat-header__actions">
          <button type="button" onClick={copyInvite}>
            {t('chat.invite')}
          </button>
          <button type="button" onClick={() => (view.value = 'settings')} aria-label={t('chat.settings')}>
            <SettingsIcon />
          </button>
          <button type="button" onClick={signOut}>
            {t('chat.signOut')}
          </button>
        </div>
      </header>

      {inviteMsg && <p className="invite-banner">{inviteMsg}</p>}
      {uploadProgress && (
        <div className="upload-progress" role="status" aria-live="polite">
          <p>
            {t(
              uploadProgress.phase === 'compressing' ? 'chat.compressingPhoto' : 'chat.uploadingPhoto',
              { index: uploadProgress.index, total: uploadProgress.total },
            )}
          </p>
          <div className="upload-progress__track">
            <div
              className="upload-progress__fill"
              style={{
                width: `${
                  (((uploadProgress.index - 1 + (uploadProgress.phase === 'uploading' ? 0.5 : 0)) /
                    uploadProgress.total) *
                    100
                  ).toFixed(0)
                }%`,
              }}
            />
          </div>
        </div>
      )}

      <div className="message-list">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`bubble ${m.senderId === s.userId ? 'bubble--mine' : ''}`}
            onContextMenu={(e) => {
              e.preventDefault();
              setBubbleMenu({ message: m });
            }}
            onTouchStart={() => startLongPress(() => setBubbleMenu({ message: m }))}
            onTouchEnd={cancelLongPress}
            onTouchMove={cancelLongPress}
          >
            {m.replyTo && (
              <div className="bubble__quote">
                <span>{quotedExcerpt(m.replyTo)}</span>
              </div>
            )}
            {m.kind === 'image' && m.media && m.media.length > 0 && (
              <div
                className={`photo-grid photo-grid--${
                  // 4 photos as a 3-col grid leaves an unbalanced 3+1 row —
                  // reuse the 2-col layout instead, giving a proper 2x2 square.
                  m.media.length === 4 ? 2 : Math.min(m.media.length, 3)
                }`}
              >
                {m.media.map((med, i) => (
                  <button
                    key={med.id}
                    type="button"
                    className="photo-grid__item"
                    onClick={() => setViewer({ media: m.media!, startIndex: i })}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setBubbleMenu({ message: m, media: med });
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      startLongPress(() => setBubbleMenu({ message: m, media: med }));
                    }}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                  >
                    {med.thumb && <img src={med.thumb} alt="" />}
                  </button>
                ))}
              </div>
            )}
            {m.kind === 'image' && m.media && m.media.length > 0 && (
              <span className="photo-grid__size">
                {formatBytes(m.media.reduce((sum, med) => sum + (med.bytes ?? 0), 0))}
              </span>
            )}
            {m.text && <p>{m.text}</p>}
            <span className="bubble__meta">
              {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {m.senderId === s.userId &&
                ` · ${m.pending ? t('meta.sending') : m.readAt ? t('meta.read') : m.deliveredAt ? t('meta.delivered') : t('meta.sent')}`}
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="composer-wrap">
        {replyingTo && (
          <div className="composer-preview">
            <span>{quotedExcerpt(replyingTo.id)}</span>
            <button type="button" onClick={() => setReplyingTo(null)} aria-label={t('reply.cancel')}>
              <CloseIcon size={16} />
            </button>
          </div>
        )}
        {pendingPhotos && (
          <div className="composer-preview composer-preview--photos">
            <div className="composer-preview__thumbs">
              {pendingPhotos.previewUrls.map((url) => (
                <img key={url} src={url} alt="" />
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                pendingPhotos.previewUrls.forEach((u) => URL.revokeObjectURL(u));
                setPendingPhotos(null);
              }}
              aria-label={t('reply.cancel')}
            >
              <CloseIcon size={16} />
            </button>
          </div>
        )}
        <form className="composer" onSubmit={submitComposer}>
          <button
            type="button"
            className="composer__photo"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!uploadProgress}
            aria-label={t('chat.sendPhotos')}
          >
            <CameraIcon />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              const target = e.target as HTMLInputElement;
              const files = target.files ? Array.from(target.files).slice(0, MAX_PHOTOS_PER_SEND) : [];
              target.value = '';
              if (files.length === 0) return;
              setPendingPhotos({ files, previewUrls: files.map((f) => URL.createObjectURL(f)) });
            }}
          />
          <input
            value={draft}
            onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
            placeholder={pendingPhotos ? t('chat.captionPlaceholder') : t('chat.messagePlaceholder')}
            autocomplete="off"
          />
          <button
            type="submit"
            className="composer__send"
            disabled={(!draft.trim() && !pendingPhotos) || !!uploadProgress}
            aria-label={t('chat.send')}
          >
            <SendIcon />
          </button>
        </form>
      </div>

      {viewer && (
        <ImageViewer
          media={viewer.media}
          startIndex={viewer.startIndex}
          accessToken={s.accessToken}
          onClose={() => setViewer(null)}
        />
      )}

      {bubbleMenu && (
        <div className="photo-menu__backdrop" onClick={() => setBubbleMenu(null)}>
          <div className="photo-menu" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setReplyingTo(bubbleMenu.message);
                setBubbleMenu(null);
              }}
            >
              {t('reply.action')}
            </button>
            {bubbleMenu.media && (
              <button type="button" onClick={() => downloadPhoto(bubbleMenu.media!)}>
                {t('viewer.menu.download')}
              </button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
